'use client';

/**
 * Cloudflare Turnstile token manager for public checkout mutations.
 *
 * The API gates the publicDonation checkout mutations behind withTurnstile()
 * (see apps/api/src/trpc/middleware/turnstile.ts). Tokens are single-use at
 * siteverify, so every gated mutation needs a freshly minted token — this
 * module owns one invisible widget and mints a new token per request.
 *
 * The widget renders in interaction-only mode into a fixed container that is
 * invisible for virtually all donors. When Cloudflare decides to challenge, the
 * container becomes a centred full-screen overlay so the checkbox is where the
 * donor is looking — parked in a corner it went unnoticed behind "Preparing…"
 * spinners and the token timed out (p50 30.5s in PostHog, ~6% of UWR
 * checkouts). Tokens are requested lazily at mutation time (via the tRPC link
 * in lib/trpc.ts), so no page needs to mount anything.
 */

import { capture } from '@/tenants/_shared/lib/analytics';

type TurnstileRenderOptions = {
  sitekey: string;
  action?: string;
  callback: (token: string) => void;
  'error-callback': (errorCode?: string) => void;
  'expired-callback'?: () => void;
  'before-interactive-callback'?: () => void;
  'after-interactive-callback'?: () => void;
  execution?: 'render' | 'execute';
  appearance?: 'always' | 'execute' | 'interaction-only';
};

type TurnstileApi = {
  render: (element: HTMLElement, options: TurnstileRenderOptions) => string;
  execute: (widgetId: string) => void;
  reset: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    __impactTurnstileOnload?: () => void;
  }
}

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__impactTurnstileOnload&render=explicit';

/** How long we wait for a silent (non-interactive) token before giving up.
 * The no-token request still reaches the API, which decides per
 * TURNSTILE_ENFORCEMENT. */
const TOKEN_TIMEOUT_MS = 30_000;
/** Once the challenge goes interactive a human has to find and tick a box, so
 * the deadline is re-armed to this from the moment it appears. */
const INTERACTIVE_TIMEOUT_MS = 120_000;
const TURNSTILE_CHECKOUT_ACTION = 'checkout';

let scriptPromise: Promise<TurnstileApi> | null = null;
let widgetId: string | null = null;
let container: HTMLElement | null = null;
let pendingResolve: ((token: string) => void) | null = null;
let pendingReject: ((error: Error) => void) | null = null;
/** Re-arms the in-flight mint's deadline when the challenge turns interactive. */
let pendingInteractive: (() => void) | null = null;
/** Serializes token requests — the widget can only mint one token at a time. */
let queue: Promise<unknown> = Promise.resolve();

export function isTurnstileConfigured(): boolean {
  return Boolean(process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY);
}

function loadScript(): Promise<TurnstileApi> {
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }

    window.__impactTurnstileOnload = () => {
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        reject(new Error('Turnstile script loaded without exposing window.turnstile'));
      }
    };

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Failed to load the Turnstile script'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

function ensureWidget(turnstile: TurnstileApi, siteKey: string): string {
  if (widgetId !== null && container?.isConnected) {
    return widgetId;
  }

  container = document.createElement('div');
  // Invisible until Cloudflare needs an interactive challenge; then it must be
  // visible and clickable, hence a fixed overlay rather than display:none.
  // The iframe can't be moved in the DOM without reloading the challenge, so
  // the same element restyles between "parked" and "overlay" (see setOverlay).
  container.style.position = 'fixed';
  container.style.zIndex = '2147483647';
  // Checkout runs inside a Radix Dialog, which sets `pointer-events: none` on
  // <body> while open and only re-enables it on its own portal node. This
  // container is a body child outside that portal, so without an explicit
  // `auto` it inherits `none` and an interactive challenge cannot be clicked —
  // the token then times out and the checkout is rejected in enforce mode.
  container.style.pointerEvents = 'auto';
  setOverlay(container, false);
  document.body.appendChild(container);

  const host = container;
  widgetId = turnstile.render(container, {
    sitekey: siteKey,
    action: TURNSTILE_CHECKOUT_ACTION,
    execution: 'execute',
    appearance: 'interaction-only',
    callback: (token: string) => {
      setOverlay(host, false);
      pendingResolve?.(token);
      pendingResolve = null;
      pendingReject = null;
    },
    'error-callback': (errorCode?: string) => {
      setOverlay(host, false);
      pendingReject?.(new Error(`Turnstile challenge failed${errorCode ? `: ${errorCode}` : ''}`));
      pendingResolve = null;
      pendingReject = null;
    },
    'before-interactive-callback': () => {
      setOverlay(host, true);
      capture('turnstile_interactive', { action: TURNSTILE_CHECKOUT_ACTION });
      pendingInteractive?.();
    },
    'after-interactive-callback': () => setOverlay(host, false),
  });

  return widgetId;
}

/** Parked: out of the way bottom-right. Overlay: dimmed backdrop with the
 * widget centred — Cloudflare's own "Verify you are human" copy is localised,
 * so no label of ours is needed. */
function setOverlay(el: HTMLElement, on: boolean): void {
  if (on) {
    el.style.inset = '0';
    el.style.display = 'flex';
    el.style.alignItems = 'center';
    el.style.justifyContent = 'center';
    el.style.background = 'rgba(0, 0, 0, 0.65)';
  } else {
    el.style.inset = '';
    el.style.bottom = '16px';
    el.style.right = '16px';
    el.style.display = '';
    el.style.alignItems = '';
    el.style.justifyContent = '';
    el.style.background = '';
  }
}

async function mintToken(): Promise<string> {
  const siteKey = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY;
  if (!siteKey) {
    throw new Error('NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY is not configured');
  }

  const turnstile = await loadScript();
  const id = ensureWidget(turnstile, siteKey);

  return new Promise<string>((resolve, reject) => {
    let interactive = false;
    let timeout: ReturnType<typeof setTimeout>;
    const arm = (ms: number) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        pendingResolve = null;
        pendingReject = null;
        pendingInteractive = null;
        if (container) setOverlay(container, false);
        reject(
          new Error(
            `Timed out waiting for a Turnstile token${interactive ? ' (interactive)' : ''}`,
          ),
        );
      }, ms);
    };
    arm(TOKEN_TIMEOUT_MS);

    pendingResolve = (token) => {
      clearTimeout(timeout);
      pendingInteractive = null;
      resolve(token);
    };
    pendingReject = (error) => {
      clearTimeout(timeout);
      pendingInteractive = null;
      reject(error);
    };
    pendingInteractive = () => {
      interactive = true;
      arm(INTERACTIVE_TIMEOUT_MS);
    };

    // A reused widget outlives the dialog that created it, and each new Radix
    // modal marks existing body children aria-hidden. Clear it so an
    // interactive challenge stays reachable to assistive tech and focus.
    container?.removeAttribute('aria-hidden');

    // Tokens are single-use: reset re-arms the widget, execute mints the next one.
    turnstile.reset(id);
    turnstile.execute(id);
  });
}

/**
 * Mint a fresh, single-use Turnstile token. Requests are serialized because a
 * widget can only run one challenge at a time. Throws when the site key is
 * missing, the script cannot load, or the challenge fails/times out — callers
 * decide whether to proceed without a token.
 */
export function getTurnstileToken(): Promise<string> {
  if (
    prefetched &&
    (prefetched.mintedAt === null || Date.now() - prefetched.mintedAt < PREFETCH_MAX_AGE_MS)
  ) {
    const { token } = prefetched;
    prefetched = null;
    return token;
  }
  prefetched = null;
  return mintQueued();
}

/** siteverify rejects tokens older than 300s; leave headroom for the request. */
const PREFETCH_MAX_AGE_MS = 240_000;
let prefetched: { token: Promise<string>; mintedAt: number | null } | null = null;

/**
 * Mint the next checkout token ahead of time so the next gated mutation doesn't
 * wait on a challenge. Call this when a payment form appears: a wallet sheet
 * (Google Pay, Apple Pay) covers the page while beforeSubmit runs, so an
 * interactive challenge there is invisible, the sheet times out, and a retry
 * from the sheet becomes a second charge.
 */
export function prefetchTurnstileToken(): void {
  if (!isTurnstileConfigured() || prefetched) {
    return;
  }
  const entry: { token: Promise<string>; mintedAt: number | null } = {
    token: mintQueued(),
    mintedAt: null,
  };
  entry.token.then(
    () => {
      entry.mintedAt = Date.now();
    },
    () => {
      if (prefetched === entry) prefetched = null;
    },
  );
  prefetched = entry;
}

function mintQueued(): Promise<string> {
  const request = queue.then(() => mintToken());
  // Keep the queue alive after failures so the next request still runs.
  queue = request.catch(() => undefined);
  return request;
}

/**
 * True when an error came back from the API's Turnstile gate rather than the
 * checkout itself. Every rejection thrown by withTurnstile()
 * (apps/api/src/trpc/middleware/turnstile.ts) names CAPTCHA in its message,
 * so callers can show a "complete the verification" hint instead of a generic
 * failure.
 */
export function isTurnstileRejection(error: unknown): boolean {
  return error instanceof Error && error.message.toUpperCase().includes('CAPTCHA');
}
